import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { CollectionComponent } from './collection/collection.component';
import { DetailsHomeComponent } from './details-page/details-home/details-home.component';
import { LocalStorageKeys } from './shared/LocalStorageKeys';
import { StorefrontComponent } from './storefront/storefront/storefront.component';
import { TvVerticalMenuComponent } from './storefront/tv-vertical-menu/tv-vertical-menu.component';

interface CustomDetachedRouteHandle extends DetachedRouteHandle {
  componentRef: any;
  contexts: any;
  route: any;
}

export class CustomReuseStrategy implements RouteReuseStrategy {
  private routeStore = new Map<string, CustomDetachedRouteHandle>();

  /**
   * This will be called first while leaving from one route to another to determine whether the component can be detached
   * @param route
   * @returns
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return !!route.data.shouldReuse;
  }

  /**
   * When shouldDetach returned true
   * This method will be called and here we can have store the component instance.
   *
   * @param route
   * @param handle
   */
  store(route: ActivatedRouteSnapshot, handle: CustomDetachedRouteHandle): void {
    if (handle && route) {
      const key = this.getResolvedUrl(route);
      if (key.includes('//tvmenu/')) {
        // check if the children should be saved
        if (route.children.length > 0 && !!!route.children[0].data.shouldReuse) {
          return;
        }
      }
      this.routeStore.set(key, handle);
      if (!!route.data.shouldStop) {
        try {
          handle?.componentRef?.instance?.stop();
        } catch (error) {
          console.error('[RS] STOP ERROR: if the component uses the shouldStop flag, should implement the stop function', error);
        }
      }
    }
  }

  /**
   * This method is used to determine whether the component that we are
   * trying to load should be re-used or not
   * @param route
   * @returns
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (localStorage?.getItem(LocalStorageKeys.CLEAN_ROUTES)) {
      this.routeStore.clear();
      localStorage?.removeItem(LocalStorageKeys.CLEAN_ROUTES);
      return false;
    }
    return !!route.routeConfig && !!this.routeStore.get(this.getResolvedUrl(route));
  }

  /**
   * When shouldAttach returns true
   * this method will be called, and here we need to retrieve and return the component instance stored
   *
   * @param route
   * @returns
   */
  retrieve(route: ActivatedRouteSnapshot): CustomDetachedRouteHandle {
    if (!route.routeConfig) return null;
    // remove all saved routes after sign out, error or language change
    if (localStorage?.getItem(LocalStorageKeys.CLEAN_ROUTES)) {
      this.routeStore.clear();
      localStorage?.removeItem(LocalStorageKeys.CLEAN_ROUTES);
      return undefined;
    }
    // clean saved routes if the langage was changed
    if (localStorage?.getItem(LocalStorageKeys.CHANGED_LANGUAGE) === 'true') {
      this.routeStore.clear();
      localStorage?.removeItem(LocalStorageKeys.CHANGED_LANGUAGE);
      return undefined;
    }
    const path = this.getResolvedUrl(route);
    const instance = this.routeStore.get(path);
    if (instance) {
      this.reload(path, instance, route);
    }
    return instance;
  }

  /**
   * This method is used to determine whether the route should be re-used or not
   *
   * @param future
   * @param curr
   * @returns
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return (
      future.routeConfig === curr.routeConfig ||
      (!!future.routeConfig?.component && future.routeConfig?.component === curr.routeConfig?.component)
    );
  }

  /**
   * Get url with values
   *
   * @param route
   * @returns
   */
  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    const url = route.pathFromRoot.map((v) => v.url.map((segment) => segment.toString()).join('/')).join('/');
    if (url === '//') {
      // tv menu path
      return url + 'tvmenu/' + this.getChildUrl(route);
    }
    return url;
  }

  private getChildUrl(route: ActivatedRouteSnapshot): string {
    return route.children.map((v) => v.url.map((segment) => segment.toString()).join('/')).join('/');
  }

  /**
   * Check component type and call the reload methods if is needed
   *
   * @param key
   * @param value
   * @param snapshot
   */
  private reload(_: string, value: any, snapshot: ActivatedRouteSnapshot) {
    if (!!snapshot.data.isTvMenu) {
      const component: TvVerticalMenuComponent = value?.componentRef?.instance;
      this.reloadTvMenu(component, snapshot);
    } else if (!!snapshot.data.isStorefront) {
      const component: StorefrontComponent = value?.componentRef?.instance;
      this.reloadStorefront(component, snapshot);
    } else if (!!snapshot.data.isDetails) {
      const component: DetailsHomeComponent = value?.componentRef?.instance;
      this.reloadDetailsPage(component, snapshot);
    } else if (!!snapshot.data.isCollection) {
      const component: CollectionComponent = value?.componentRef?.instance;
      this.reloadCollectionPage(component);
    } else {
      // console.log('[RS] RELOAD', key);
    }
  }

  /**
   * reload tv menu component
   *
   * @param component
   */
  private reloadTvMenu(component: TvVerticalMenuComponent, snapshot: ActivatedRouteSnapshot): void {
    if (!component.saved) {
      let section = '';
      if (!!snapshot.firstChild?.data.isEPG) {
        section = 'EPG';
      } else if (!!snapshot.firstChild?.data.isSearch) {
        section = 'SEARCH';
      } else if (!!snapshot.firstChild?.data.isSettings) {
        section = 'SETTINGS';
      } else if (!!snapshot.firstChild?.data.isCollection) {
        section = 'COLLECTION';
      } else {
        section = 'SF';
      }
      component.reload(section);
    }
    component.saved = !component.saved;
  }

  /**
   * Reload storefront component
   *
   * @param component
   * @param snapshot
   */
  private reloadStorefront(component: StorefrontComponent, snapshot: ActivatedRouteSnapshot): void {
    // validate if storefront was already loaded
    if (!component.saved) {
      component.reload(snapshot.data.storefrontData, snapshot.params.id, !!localStorage?.getItem(LocalStorageKeys.TAB_CHANGED));
    }
    // avoid reload twice execution
    component.saved = !component.saved;
  }

  /**
   * Reload details page
   *
   * @param component
   * @param snapshot
   */
  private reloadDetailsPage(component: DetailsHomeComponent, snapshot: ActivatedRouteSnapshot): void {
    if (!component.saved) {
      let resource = component.resourceVm;
      if (snapshot.data.detailsData[0]) {
        resource = snapshot.data.detailsData[0];
      }
      component.loadFocus();
      component.reloadVariables();
      component.addContainerData(resource);
      component.triggerDetailsEvent();
      component.reloadPersonalization();
    }
    component.saved = !component.saved;
  }

  /**
   * Reload details page
   *
   * @param component
   * @param snapshot
   */
  private reloadCollectionPage(component: CollectionComponent): void {
    if (!component.saved) {
      component.loadFocus();
    }
    component.saved = !component.saved;
  }
}
